import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import { MY_PATH_URL } from '../../paths/utils/pathsChatUrl';
import { caseStudyAPI } from '../../case-study/api/casestudyAPI';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black, Logo_black_iconOnly } from '../../../components/Logo/Logo';
import { useProductBackHandler } from '../../../components/ProductBackButton/ProductBackProvider';
import { scrollDocumentToTop, useScrollDocumentToTop } from '../../../lib/scrollDocumentToTop';
import { TEST_IMAGE_URLS, TEST_IMAGE_ORDER } from '../config/testImageUrls';
import { getTutorialIconLabel, resolveAppLaunchTarget, navigateAppLaunch, isStandaloneReasoningApp, resolveStandaloneReasoningLaunch } from '../utils/appDisplayUtils';
import '../../quiz-generation/components/QuizGeneratorPage.css';
import './AppDetailPage.css';
import SkeletonLoader from './SkeletonLoader';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';

const FLAG_TO_KEY = {
  has_logical_reasoning: 'logical',
  has_pattern_recognition: 'pattern',
  has_numerical_reasoning: 'numerical',
  has_verbal_reasoning: 'verbal',
  has_video_interview: 'video_interview',
};

const TEST_IMAGE_LABELS = {
  logical: 'Logical',
  pattern: 'Pattern',
  numerical: 'Numerical',
  verbal: 'Verbal',
  video_interview: 'Video Interview',
};

/**
 * Standalone Logical / Numerical / Pattern / Verbal apps → /website-reasoning-test?type=
 * (never the old /apps-store/:id/experience suite shell).
 */
const isReasoningOnlyApp = (a) => isStandaloneReasoningApp(a);

const AppDetailPage = ({ fromLanding = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [app, setApp] = useState(null);
  const [suggestedApps, setSuggestedApps] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isOwned, setIsOwned] = useState(false);
  const [isBuying, setIsBuying] = useState(false);

  useScrollDocumentToTop('detail');

  const userName = user?.name || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  // Format rating count (e.g., 1200 -> "1.2K", 500 -> "500")
  const formatRatingCount = (count) => {
    if (!count || count === 0) return '0';
    if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count.toString();
  };

  const formatReviewCount = (app) => {
    if (!app) return '12.5K';
    if (app.title === 'Toca Boca World') return '6.68M';
    const count = app.rating_count || 0;
    let scaledCount = count;
    if (scaledCount === 0) {
      scaledCount = 12480;
    } else if (scaledCount < 10) {
      scaledCount = scaledCount * 1420000;
    } else if (scaledCount < 100) {
      scaledCount = scaledCount * 125000;
    } else {
      scaledCount = scaledCount * 12500;
    }

    if (scaledCount >= 1000000) {
      return (scaledCount / 1000000).toFixed(2) + 'M';
    }
    if (scaledCount >= 1000) {
      return (scaledCount / 1000).toFixed(1) + 'K';
    }
    return scaledCount.toString();
  };

  const getDownloads = (app) => {
    if (!app) return '10K+';
    if (app.title === 'Windmill') return '10M+';
    if (app.title === 'Toca Boca World') return '100M+';
    const base = (app.rating_count || 127) * 15200;
    if (base >= 100000000) return '100M+';
    if (base >= 50000000) return '50M+';
    if (base >= 10000000) return '10M+';
    if (base >= 5000000) return '5M+';
    if (base >= 1000000) return '1M+';
    if (base >= 500000) return '500K+';
    if (base >= 100000) return '100K+';
    if (base >= 50000) return '50K+';
    return '10K+';
  };

  const launchDirectContent = (appToStart) => {
    if (!appToStart) return;

    // Standalone reasoning → /website-reasoning-test?type= (never old experience suite)
    const reasoningLaunch = resolveStandaloneReasoningLaunch(appToStart);
    if (reasoningLaunch) {
      navigateAppLaunch(navigate, reasoningLaunch, { replace: true });
      return;
    }

    const examName = (appToStart.exam_name || '').trim().toLowerCase();
    const titleLower = (appToStart.title || '').trim().toLowerCase();
    const appIdLower = (String(appToStart.id) || '').trim().toLowerCase();
    const appTypeLower = (appToStart.app_type || '').trim().toLowerCase();

    // 1. Application Builder tools
    if (titleLower.includes('cv') || titleLower.includes('cover letter') || titleLower.includes('personal statement') || appIdLower.includes('cv') || appIdLower.includes('cover_letter') || appIdLower.includes('personal_statement')) {
      const tool = examName || (titleLower.includes('cover letter') ? 'cover_letter' : titleLower.includes('personal statement') ? 'personal_statement' : 'cv');
      navigate(`/application-builder/form/${tool}`, { replace: true });
      return;
    }

    // 2. Code Lab
    if (titleLower.includes('code lab') || appIdLower.includes('code_lab') || examName === 'code_lab') {
      navigate('/code-lab', { replace: true });
      return;
    }

    // 3. Smart Library
    if (titleLower.includes('library') || appIdLower.includes('library') || examName === 'library') {
      navigate('/library', { replace: true });
      return;
    }

    // 3b. Supercurricular Books
    if (
      examName === 'supercurricular_books' ||
      titleLower.includes('supercurricular') ||
      appIdLower.includes('supercurricular')
    ) {
      navigate('/supercurricular-books', { replace: true });
      return;
    }

    // 4. Task List
    if (titleLower.includes('task') || appIdLower.includes('task') || examName === 'task_list') {
      navigate('/task-list', { replace: true });
      return;
    }

    // 5. Interview Simulator
    if (titleLower.includes('interview') || appIdLower.includes('interview') || examName.includes('interview')) {
      navigate('/interview', { replace: true });
      return;
    }

    // 6. Financial Modeling Lab
    if (titleLower.includes('financial model') || appIdLower.includes('financial_model') || examName === 'financial_modeling_lab') {
      navigate('/financial-modeling-lab', { replace: true });
      return;
    }

    // 7. Case Studies
    if (appTypeLower === 'case' || titleLower.includes('case') || appIdLower.includes('case')) {
      if (appToStart.case_study_id) {
        navigate(`/case-study/${appToStart.case_study_id}`, { replace: true });
      } else {
        navigate('/cases', { replace: true });
      }
      return;
    }

    // 8. Exam Apps (TMUA, LNAT, UCAT, ESAT, TARA, LSAT, STEP)
    if (examName === 'tmua' || titleLower.includes('tmua')) {
      navigate('/practice/tmua', { replace: true });
      return;
    }
    if (examName === 'lnat' || titleLower.includes('lnat')) {
      navigate('/practice/lnat', { replace: true });
      return;
    }
    if (examName === 'ucat' || titleLower.includes('ucat')) {
      navigate('/practice/ucat', { replace: true });
      return;
    }
    if (examName === 'esat' || titleLower.includes('esat')) {
      navigate('/practice/esat', { replace: true });
      return;
    }
    if (examName === 'tara' || titleLower.includes('tara')) {
      navigate('/practice/tara', { replace: true });
      return;
    }
    if (examName === 'lsat' || titleLower.includes('lsat')) {
      navigate('/practice/lsat', { replace: true });
      return;
    }
    if (examName === 'step' || titleLower.includes('step')) {
      navigate('/practice/step', { replace: true });
      return;
    }

    // 9. Interactive tool experiences (FlowForge, Flash Cards, Caius Explore)
    if (examName === 'caius_explore' || titleLower === 'caius explore') {
      navigate(`/apps-store/${appToStart.id}/caius-explore`, { replace: true });
      return;
    }
    if (examName === 'flash_cards' || titleLower === 'flash cards') {
      navigate(`/apps-store/${appToStart.id}/flash-cards`, { replace: true });
      return;
    }
    if (examName === 'flowforge' || titleLower === 'flowforge') {
      navigate(`/apps-store/${appToStart.id}/flowforge`, { replace: true });
      return;
    }
    if (examName === 'custom_quiz_generator' || titleLower.includes('custom quiz')) {
      navigate('/custom-quiz-generator', { replace: true });
      return;
    }

    // 10. Direct content path if defined and not a store/cover page
    if (appToStart.path && appToStart.path.startsWith('/') && !appToStart.path.includes('/apps-store/') && !appToStart.path.includes('/store/')) {
      navigate(appToStart.path, { replace: true });
      return;
    }

    // Default direct experience
    if (appToStart.id) {
      navigate(`/apps-store/${appToStart.id}/experience`, { replace: true });
      return;
    }
  };

  // Fetch app details
  useEffect(() => {
    const fetchApp = async () => {
      try {
        setLoading(true);
        const usePublicApi = fromLanding && !isAuthenticated;
        const appData = usePublicApi
          ? await appsAPI.getAppByIdPublic(id)
          : await appsAPI.getAppById(id);
        setApp(appData);

        // If user is authenticated and app is owned/part of package, immediately bypass cover page
        if (!fromLanding && isAuthenticated && appData && (appData.is_owned || appData.is_unlocked)) {
          setIsOwned(true);
          launchDirectContent(appData);
          return;
        }

        // Check ownership status if logged in
        if (!usePublicApi && appData.is_owned !== undefined) {
          setIsOwned(appData.is_owned);
          if (appData.is_owned) {
            launchDirectContent(appData);
            return;
          }
        } else if (!usePublicApi) {
          setIsOwned(false);
        }

        // If tutorial app (tests), fetch quizzes
        if (appData.app_type === 'tutorial') {
          const reasoningOnly = !appData.case_study_id && (
            appData.has_logical_reasoning || appData.has_pattern_recognition ||
            appData.has_numerical_reasoning || appData.has_verbal_reasoning
          );
          if (!reasoningOnly) {
            setLoadingQuizzes(true);
            try {
              const quizzesData = fromLanding
                ? await appsAPI.getAppQuizzesPublic(id)
                : await appsAPI.getAppQuizzes(id);
              setQuizzes(quizzesData.quizzes || []);
            } catch (error) {
              console.error('Error fetching quizzes:', error);
            } finally {
              setLoadingQuizzes(false);
            }
          }
        }

        // Paint cover first — related suggestions are non-critical.
        setLoading(false);

        // Fetch all apps and case studies to find subject-matched suggestions
        let appsList = [];
        let caseStudiesList = [];
        try {
          const [allApps, allCaseStudies] = await Promise.all([
            fromLanding ? appsAPI.getAppsPublic() : appsAPI.getApps(),
            caseStudyAPI.getCaseStudies({ limit: 1000 }).catch(() => [])
          ]);
          appsList = Array.isArray(allApps) ? allApps : [];
          caseStudiesList = Array.isArray(allCaseStudies) ? allCaseStudies : [];
        } catch (e) {
          appsList = [];
          caseStudiesList = [];
        }

        // Combine pool of apps and case studies
        const combinedPool = [...appsList, ...caseStudiesList];

        // Find strictly related apps/cases (same subject/law/CS/admissions test only)
        const findRelatedApps = (currentApp, list) => {
          if (!currentApp || !Array.isArray(list)) return [];
          const currentId = String(currentApp.id);
          const title = (currentApp.title || '').trim().toLowerCase();
          const subj = (currentApp.subject || '').trim().toLowerCase();
          const ind = (currentApp.industry || '').trim().toLowerCase();
          const desc = (currentApp.description || '').trim().toLowerCase();
          const fullText = `${title} ${subj} ${ind} ${desc}`;

          const others = list.filter(a => String(a.id) !== currentId);

          const isLawApp = fullText.includes('lnat') || fullText.includes('law') || fullText.includes('legal');
          const isCSApp = fullText.includes('tmua') || fullText.includes('computer science') || fullText.includes('cs') || fullText.includes('python') || fullText.includes('code');
          const isEngApp = fullText.includes('engineering') || fullText.includes('aerospace') || fullText.includes('fluid') || fullText.includes('windmill');
          const isFinApp = fullText.includes('finance') || fullText.includes('banking') || fullText.includes('dcf') || fullText.includes('lbo') || fullText.includes('valuation');
          const isMedApp = fullText.includes('medicine') || fullText.includes('medical') || fullText.includes('ucat') || fullText.includes('crispr');

          const domainMatches = others.filter((appItem) => {
            const aTitle = (appItem.title || '').toLowerCase();
            const aSubj = (appItem.subject || '').trim().toLowerCase();
            const aInd = (appItem.industry || '').trim().toLowerCase();
            const aText = `${aTitle} ${aSubj} ${aInd}`;

            if (isLawApp) {
              return aSubj.includes('law') || aInd.includes('law') || aText.includes('law') || aText.includes('legal') || aText.includes('equality') || aText.includes('precedent') || aText.includes('court') || aText.includes('statute') || aText.includes('arbitration') || aText.includes('brown v');
            }
            if (isCSApp) {
              return (aSubj.includes('computer science') || aSubj.includes('cs') || aInd.includes('computer science') || aText.includes('code') || aText.includes('python') || aText.includes('algorithm') || aText.includes('microservice') || aText.includes('rust')) && !aSubj.includes('law') && !aSubj.includes('engineering') && !aSubj.includes('medicine');
            }
            if (isEngApp) {
              return (aSubj.includes('engineering') || aInd.includes('engineering') || aText.includes('engineering') || aText.includes('aerospace') || aText.includes('composite') || aText.includes('windmill')) && !aSubj.includes('law') && !aSubj.includes('medicine');
            }
            if (isFinApp) {
              return (aSubj.includes('investment banking') || aSubj.includes('finance') || aInd.includes('finance') || aText.includes('valuation') || aText.includes('lbo') || aText.includes('dcf')) && !aSubj.includes('law');
            }
            if (isMedApp) {
              return (aSubj.includes('medicine') || aInd.includes('medicine') || aText.includes('medicine') || aText.includes('medical') || aText.includes('diagnosis') || aText.includes('crispr')) && !aSubj.includes('law') && !aSubj.includes('engineering');
            }
            return false;
          });



          const resultList = [...domainMatches];
          for (const item of others) {
            if (resultList.length >= 4) break;
            if (!resultList.some(r => String(r.id) === String(item.id) || r.title === item.title)) {
              resultList.push(item);
            }
          }

          return resultList.slice(0, 4);
        };

        const suggestions = findRelatedApps(appData, combinedPool);
        setSuggestedApps(suggestions);
      } catch (error) {
        console.error('Error fetching app:', error);
        setApp(null);
        setSuggestedApps([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchApp();
    }

    // Listen for app purchase events to refresh ownership status
    const handleAppPurchased = (event) => {
      if (event.detail?.appId === id) {
        // Refresh app data to get updated ownership status
        const refreshApp = async () => {
          try {
            if (!fromLanding) {
              const appData = await appsAPI.getAppById(id);
              setApp(appData);
              if (appData.is_owned !== undefined) {
                setIsOwned(appData.is_owned);
              }
            }
          } catch (error) {
            console.error('Error refreshing app after purchase:', error);
          }
        };
        refreshApp();
      }
    };

    window.addEventListener('appPurchased', handleAppPurchased);

    return () => {
      window.removeEventListener('appPurchased', handleAppPurchased);
    };
  }, [id, fromLanding]);

  const handleAppClick = (appId) => {
    navigate((fromLanding && !isAuthenticated) ? `/store/${appId}` : `/apps-store/${appId}`);
    scrollDocumentToTop();
  };

  // Start app handler - purchases app if not owned, then starts it
  const handleStartApp = async () => {
    if (fromLanding && !isAuthenticated) {
      const isProductivityApp = app?.app_type === 'productivity' || app?.app_type === 'utility'; // cv, cover letter, personal statement, my path, notes, task list, uai
      const isTutorialApp = app?.app_type === 'tutorial';
      const interviewExamNames = [
        'medicine_mmi_interview',
        'medicine_panel_interview',
        'oxbridge_interview',
        'interview_simulator',
        'investment_banking_interview',
        'private_equity_interview',
        'engineering_interview',
        'computer_science_interview',
        'law_interview',
        'operations_fs_interview',
        'scholarship_interview',
      ];
      const isInterview = isTutorialApp && interviewExamNames.includes((app?.exam_name || '').trim().toLowerCase());
      const isCaius = isTutorialApp && ((app?.exam_name || '').trim().toLowerCase() === 'caius_explore' || (app?.title || '').trim().toLowerCase() === 'caius explore');
      // Reasoning-only apps → direct /website-reasoning-test?type= (never old experience suite)
      const reasoningLaunch = resolveAppLaunchTarget(app);
      if (isReasoningOnlyApp(app) && reasoningLaunch) {
        navigate('/login', {
          state: {
            intendedAppId: id,
            intendedPurchase: true,
            returnTo: reasoningLaunch,
          }
        });
        return;
      }
      const intendedRoute = isProductivityApp ? 'tool' : (isCaius ? 'tool' : (isTutorialApp ? (isInterview ? 'tool' : 'tests') : 'experience'));
      navigate('/login', {
        state: {
          intendedAppId: id,
          intendedPurchase: true,
          intendedRoute,
          // Pass intendedToolType for productivity tools, interview tools, and Caius Explore
          ...((isProductivityApp || isInterview || isCaius) && { intendedToolType: isCaius ? 'caius_explore' : (app?.exam_name || 'cv') })
        }
      });
      return;
    }

    try {
      setIsBuying(true);

      let appToStart = app;

      if (!isOwned) {
        await appsAPI.buyApp(id);
        setIsOwned(true);
        const appData = await appsAPI.getAppById(id);
        setApp(appData);
        appToStart = appData;
        window.dispatchEvent(new CustomEvent('appPurchased', { detail: { appId: id } }));
      }

      // Prefer canonical launch map (standalone reasoning → /website-reasoning-test?type=)
      const launchTarget = resolveAppLaunchTarget(appToStart);
      if (launchTarget) {
        // replace so browser Back cannot return to old /apps-store/:id/experience|detail suite
        navigateAppLaunch(
          navigate,
          launchTarget,
          isReasoningOnlyApp(appToStart) ? { replace: true } : undefined
        );
        return;
      }

      // Navigate based on app type: case, tutorial, productivity, utility
      if (appToStart?.app_type === 'productivity' || appToStart?.app_type === 'utility') {
        const examName = (appToStart.exam_name || '').trim().toLowerCase();
        const titleLower = (appToStart.title || '').trim().toLowerCase();
        const subjectLower = (appToStart.subject || '').trim().toLowerCase();
        if (examName === 'my_path') {
          navigate(MY_PATH_URL);
        } else if (examName === 'uai' || titleLower === 'uai') {
          navigate('/chat');
        } else if (examName === 'flash_cards' || titleLower === 'flash cards') {
          navigate(`/apps-store/${id}/flash-cards`);
        } else if (examName === 'notes' || titleLower === 'notes') {
          navigate('/notes');
        } else if (examName === 'library' || titleLower.includes('library') || appToStart?.app_type === 'library') {
          navigate(`/apps-store/${id}/library`);
        } else if (
          examName === 'supercurricular_books' ||
          titleLower === 'supercurricular books' ||
          titleLower.includes('supercurricular')
        ) {
          navigate(`/apps-store/${id}/supercurricular-books`);
        } else if (examName === 'task_list' || titleLower === 'task list') {
          navigate(`/apps-store/${id}/task-list`);
        } else if (examName === 'flowforge' || titleLower === 'flowforge') {
          navigate(`/apps-store/${id}/flowforge`);
        } else if (examName === 'custom_quiz_generator' || titleLower.includes('custom quiz')) {
          navigate('/custom-quiz-generator');
        } else if (
          examName === 'code_lab' ||
          titleLower === 'code lab' ||
          titleLower === 'code labs' ||
          subjectLower === 'coding'
        ) {
          navigate(`/apps-store/${id}/code-lab`);
        } else if (
          examName === 'financial_modeling_lab' ||
          titleLower === 'financial modeling lab' ||
          titleLower === 'financial modelling lab'
        ) {
          navigate('/financial-modeling-lab');
        } else if (examName === 'cv' || examName === 'cover_letter' || examName === 'personal_statement') {
          navigate(`/application-builder/form/${examName}`);
        } else {
          navigate('/application-builder/form/cv');
        }
      } else if (appToStart?.app_type === 'tutorial') {
        const examName = (appToStart.exam_name || '').trim().toLowerCase();
        if (examName === 'caius_explore' || (appToStart.title || '').trim().toLowerCase() === 'caius explore') {
          navigate(`/apps-store/${id}/caius-explore`);
        } else if (
          examName === 'medicine_mmi_interview' ||
          examName === 'medicine_panel_interview' ||
          examName === 'oxbridge_interview' ||
          examName === 'interview_simulator' ||
          examName === 'investment_banking_interview' ||
          examName === 'private_equity_interview' ||
          examName === 'engineering_interview' ||
          examName === 'computer_science_interview' ||
          examName === 'law_interview' ||
          examName === 'operations_fs_interview' ||
          examName === 'scholarship_interview'
        ) {
          navigate(`/website-interview?toolType=${encodeURIComponent(appToStart.exam_name)}`);
        } else if (appToStart.exam_name) {
          navigate(`/practice/${appToStart.exam_name.toLowerCase()}`);
        } else {
          navigate(`/apps-store/${id}/tests`);
        }
      } else {
        navigate(`/apps-store/${id}/experience`);
      }
    } catch (error) {
      console.error('Error starting app:', error);
      alert('Failed to start app: ' + (error.message || 'Unknown error'));
    } finally {
      setIsBuying(false);
    }
  };

  const backToStore = useCallback(() => {
    navigate('/apps');
    scrollDocumentToTop();
  }, [navigate]);

  useProductBackHandler(backToStore);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const getDescription = () => {
    if (!app) return '';
    return app.description || app.case_study_description || 'No description available.';
  };

  const stripHTMLTags = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  const renderHTMLContent = (html) => {
    if (!html) return null;
    const sanitized = sanitizeHtml(html);
    return <div className="app-detail-description-html" dangerouslySetInnerHTML={{ __html: sanitized }} />;
  };

  const hasHTML = (s) => (s || '').includes('<');

  const description = getDescription();
  const plainDescription = hasHTML(description) ? stripHTMLTags(description) : description;
  const descriptionWords = plainDescription.split(/\s+/).filter(Boolean);
  const shortDescription = descriptionWords.slice(0, 50).join(' ');
  const isLongDescription = descriptionWords.length > 50;

  const isExperienceApp = app?.app_type === 'case';
  const isToolApp = app?.app_type === 'productivity' || app?.app_type === 'utility'; // cv, cover letter, personal statement, my path, notes, task list, uai
  const isTutorialApp = app?.app_type === 'tutorial';

  /** Build screenshot items for case apps: 1 case image + 1 per test type (from config). */
  const getScreenshotItems = () => {
    if (!app || app.app_type !== 'case') return [];
    const items = [];
    const caseImg = app.images?.large || app.images?.medium || app.images?.small;
    if (caseImg) {
      items.push({ type: 'case', src: caseImg, alt: `${app.title} – Case` });
    }
    for (const key of TEST_IMAGE_ORDER) {
      const url = TEST_IMAGE_URLS[key];
      if (!url) continue;
      const flag = Object.keys(FLAG_TO_KEY).find((f) => FLAG_TO_KEY[f] === key);
      if (!flag || !app[flag]) continue;
      items.push({
        type: 'test',
        src: url,
        alt: TEST_IMAGE_LABELS[key] || key,
      });
    }
    return items;
  };

  if (loading) {
    return (
      <div className="app-detail-page netflix-style">
        <SkeletonLoader type="detail" count={1} />
      </div>
    );
  }

  if (!app) {
    return (
      <div className="app-detail-page netflix-style">
        <div className="app-detail-error">
          <p>App not found</p>
          <button onClick={backToStore} className="btn-back-to-store">
            Back to Ivori Apps
          </button>
        </div>
      </div>
    );
  }

  // Check if this is the Windmill app
  const isWindmillApp = app.creator === 'Ash.exe' && app.subject === 'Engineering' && app.title === 'Windmill';

  const rawCoverImage = app.images?.large || app.images?.medium || app.banner_image_url || app.cover_image_url;
  const hasValidCover = rawCoverImage &&
    (rawCoverImage.startsWith('http') || rawCoverImage.startsWith('/api/') || rawCoverImage.startsWith('/')) &&
    !rawCoverImage.endsWith('.svg');

  const heroImage = hasValidCover ? rawCoverImage : '/auth_cover.webp';

  // Windmill logo URL - replace with actual windmill logo URL when available
  const windmillLogoUrl = 'https://pathbuddy-media.lon1.cdn.digitaloceanspaces.com/windmill-logo.png'; // Update this with actual logo URL
  const appIcon = isWindmillApp
    ? windmillLogoUrl
    : (app.images?.small || app.images?.medium || '/logo192.png');

  const screenshotItems = getScreenshotItems();
  const rawDescText = app?.description || '';
  const descParagraphs = rawDescText.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  const isMultiParagraph = descParagraphs.length > 1 || rawDescText.length > 350;
  const displayedParagraphs = (isMultiParagraph && !showFullDescription) ? descParagraphs.slice(0, 1) : descParagraphs;

  return (
    <div className={`app-detail-page netflix-style${isExperienceApp ? ' experience-app' : ''}`}>
      {fromLanding && (
        <header className="app-detail-header">
          <div className="app-detail-header-content">
            <div className="app-detail-left-section">
              <div className="app-detail-logo">
                <Logo_black_iconOnly onClick={() => navigate('/')} />
              </div>
              <h1 className="app-detail-title app-detail-header-page-name">{app?.title || 'App'}</h1>
            </div>

            <div className="app-detail-header-actions">
              <button
                className="app-detail-sign-in-btn"
                onClick={() => navigate('/login')}
              >
                Sign In
              </button>
            </div>
          </div>
        </header>
      )}

      {/* Hero Section */}
      <div className={`app-detail-hero${isExperienceApp ? ' experience-hero' : ''}${app.app_type === 'tutorial' ? ' productivity-hero' : ''}${isToolApp ? ' tool-hero' : ''}${isWindmillApp ? ' windmill-hero' : ''}`}>
        {/* Transparent Back Arrow Button — provided by ProductBackProvider */}

        {/* Cover Background */}
        <div
          className="app-detail-hero-background"
          style={{
            backgroundImage: `url(${heroImage})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        >
          <div className="app-detail-hero-overlay" />
        </div>

        <div className="app-detail-hero-content">
          <div className="app-detail-header-info">
            <img
              src={app.images?.medium || app.images?.small || app.images?.large || '/logo192.png'}
              alt={app.title}
              className="app-detail-icon"
            />
            <div className="app-detail-title-section">
              <h1 className="app-detail-title">{app.title}</h1>
              {app.creator && (
                <p className="app-detail-creator">
                  By{' '}
                  <a
                    className="app-detail-creator-link"
                    onClick={(e) => {
                      e.preventDefault();
                      const slug = (app.creator_slug || app.creator).toLowerCase().replace(/\s+/g, '');
                      navigate(fromLanding ? `/creator/${encodeURIComponent(slug)}` : `/apps-store/creator/${encodeURIComponent(slug)}`);
                      scrollDocumentToTop();
                    }}
                  >
                    {app.creator}
                  </a>
                </p>
              )}
              {(() => {
                const ind = (app.industry || '').trim();
                const subj = (app.subject || '').trim();
                if (ind && subj && ind.toLowerCase() !== subj.toLowerCase()) {
                  return (
                    <p className="app-detail-publisher">
                      {ind} <span className="app-detail-category-dot">•</span> <span className="app-detail-publisher-category">{subj}</span>
                    </p>
                  );
                }
                const cat = ind || subj;
                if (!cat || (app.creator && cat.toLowerCase() === app.creator.toLowerCase())) {
                  return null;
                }
                return <p className="app-detail-publisher">{cat}</p>;
              })()}

              <div className="app-detail-hero-buttons">
                <button
                  className="app-detail-install-btn"
                  onClick={handleStartApp}
                  disabled={isBuying}
                >
                  {isBuying ? 'Starting...' : 'Start'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="app-detail-main">
        <div className="app-detail-content">
          <div className="app-detail-left-column">
            {/* Description Section */}
            <section className="app-detail-section">
              <h2 className="app-detail-section-title">Description</h2>
              <div className="app-detail-description-container">
                <div className="app-detail-description-paragraphs">
                  {hasHTML(rawDescText) ? (
                    renderHTMLContent(rawDescText)
                  ) : (
                    displayedParagraphs.map((para, idx) => (
                      <p key={idx} className="app-detail-justified-para">{para}</p>
                    ))
                  )}
                </div>

                {isMultiParagraph && (
                  <button
                    className="app-detail-read-more-btn"
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    aria-expanded={showFullDescription}
                  >
                    <span>{showFullDescription ? 'Read less' : 'Read more'}</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      className={`app-detail-read-more-chevron ${showFullDescription ? 'expanded' : ''}`}
                    >
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            </section>

            {/* Assessment Types Section (commented out for now)
            {app.assessment_types && app.assessment_types.length > 0 && (
              <section className="app-detail-section">
                <h2 className="app-detail-section-title">Assessment Types</h2>
                <div className="app-detail-assessment-types">
                  {app.assessment_types.map((type, idx) => (
                    <span key={idx} className="app-detail-assessment-badge">{type}</span>
                  ))}
                </div>
              </section>
            )}
            */}

            {/* Testimonials Section (commented out for now)
            {Array.isArray(app.reviews) && app.reviews.length > 0 && (
              <section className="app-detail-section">
                <h2 className="app-detail-section-title">Testimonials</h2>
                <div className="app-detail-reviews">
                  {app.reviews.map((review, idx) => (
                    <div key={idx} className="app-detail-review">
                      {review.author && <span className="app-detail-review-author">{review.author}</span>}
                      {review.review_text && <p className="app-detail-review-text">{review.review_text}</p>}
                      {review.rating != null && <span className="app-detail-review-rating">{Number(review.rating).toFixed(1)} ⭐</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}
            */}

            {/* Video Preview Section (for case study / simulation apps with video_urls) */}
            {app.app_type !== 'tutorial' && Array.isArray(app.video_urls) && app.video_urls.length > 0 && (
              <section className="app-detail-section">
                <h2 className="app-detail-section-title">
                  {app.app_type === 'case_study' || app.subject === 'Law' || app.subject === 'law' ? 'Case Study Preview' : 'Preview'}
                </h2>
                <div className="app-detail-videos">
                  {app.video_urls.map((videoUrl, idx) => (
                    <div key={idx} className="app-detail-video-container">
                      <video
                        className="app-detail-video"
                        controls
                        preload="metadata"
                        poster={app.images?.large || app.images?.medium || app.images?.small}
                      >
                        <source src={videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Attached preview (case apps that include an interactive simulation/game) */}
            {app.app_type !== 'tutorial' && app.attached_game && Array.isArray(app.attached_game.video_urls) && app.attached_game.video_urls.length > 0 && (
              <section className="app-detail-section">
                <h2 className="app-detail-section-title">
                  {app.subject === 'Law' || app.subject === 'law' || app.app_type === 'case_study' ? 'Includes: Interactive Simulation' : 'Includes: Interactive Preview'}
                </h2>
                <div className="app-detail-videos">
                  {app.attached_game.video_urls.map((videoUrl, idx) => (
                    <div key={idx} className="app-detail-video-container">
                      <video
                        className="app-detail-video"
                        controls
                        preload="metadata"
                        poster={app.images?.large || app.images?.medium || app.images?.small}
                      >
                        <source src={videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Screenshots Section (commented out for now)
            {app.app_type !== 'tutorial' && (!Array.isArray(app.video_urls) || app.video_urls.length === 0) && (
              <section className="app-detail-section">
                <h2 className="app-detail-section-title">Screenshots</h2>
                <div className="app-detail-screenshots">
                  {screenshotItems.length > 0 ? (
                    screenshotItems.map((item, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="app-detail-screenshot"
                        onClick={() => setLightboxImage(item)}
                        aria-label={`View ${item.alt}`}
                      >
                        <img src={item.src} alt={item.alt} title={`Click to enlarge: ${item.alt}`} />
                        <span className="app-detail-screenshot-label">{item.alt}</span>
                      </button>
                    ))
                  ) : (
                    <div className="app-detail-screenshot-placeholder">
                      <p>No screenshots available</p>
                    </div>
                  )}
                </div>
              </section>
            )}
            */}

            {/* Lightbox: click screenshot to view full-size in center */}
            {lightboxImage && (
              <div
                className="app-detail-lightbox-overlay"
                onClick={() => setLightboxImage(null)}
                role="button"
                tabIndex={0}
                aria-label="Close"
                onKeyDown={(e) => e.key === 'Enter' && setLightboxImage(null)}
              >
                <button
                  type="button"
                  className="app-detail-lightbox-close"
                  onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }}
                  aria-label="Close"
                >
                  ×
                </button>
                <div
                  className="app-detail-lightbox-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img src={lightboxImage.src} alt={lightboxImage.alt} />
                  <span className="app-detail-lightbox-caption">{lightboxImage.alt}</span>
                </div>
              </div>
            )}
            {/* Discover More Section - Placed directly under Description */}
            <section className="app-detail-discover-more-section">
              <h2 className="app-detail-sidebar-title">Discover more</h2>
              <div className="app-detail-suggestions-grid">
                {suggestedApps.length > 0 ? (
                  suggestedApps.map((suggestedApp) => {
                    const imageUrl = suggestedApp.images?.medium || suggestedApp.images?.large || suggestedApp.banner_image_url || suggestedApp.cover_image_url || '/logo-yellow.png';
                    const rawDesc = suggestedApp.description || suggestedApp.tagline || suggestedApp.short_description || '';
                    let cleanDesc = rawDesc.replace(/<[^>]*>?/gm, '').trim();
                    if (!cleanDesc) {
                      cleanDesc = `Explore strategic roleplay and interactive practice modules with ${suggestedApp.title}.`;
                    }

                    return (
                      <div
                        key={suggestedApp.id}
                        role="button"
                        tabIndex={0}
                        className="masonry-case-card app-detail-masonry-card"
                        onClick={() => handleAppClick(suggestedApp.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAppClick(suggestedApp.id)}
                      >
                        <div className="masonry-card-image-wrapper">
                          <img
                            src={imageUrl}
                            alt={suggestedApp.title}
                            className="masonry-card-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/logo-yellow.png';
                            }}
                          />
                          {(suggestedApp.subject || suggestedApp.industry) && (
                            <span className="masonry-card-subject-tag">
                              {suggestedApp.subject || suggestedApp.industry}
                            </span>
                          )}
                          <span className="masonry-circle-arrow-btn" aria-hidden="true">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="7" y1="17" x2="17" y2="7"></line>
                              <polyline points="7 7 17 7 17 17"></polyline>
                            </svg>
                          </span>
                        </div>
                        <div className="masonry-card-body">
                          <h3 className="masonry-card-title">{suggestedApp.title}</h3>
                          <p className="masonry-card-desc">{cleanDesc}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="app-detail-no-suggestions">No suggestions available</p>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AppDetailPage;
